// frontend/src/api/index.js
/**
 * Base API configuration for making requests to the backend
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001/api';

// Default headers
const defaultHeaders = {
  'Content-Type': 'application/json',
};

// Helper to get auth header
const getAuthHeader = () => {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Generic API request function
 */
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  let currentOptions = { ...options };

  const isFormData = currentOptions.body instanceof FormData;
  
  currentOptions.headers = {
    ...(isFormData ? {} : defaultHeaders),
    ...currentOptions.headers,
    ...getAuthHeader(),
  };

  if (
    ['POST', 'PUT', 'DELETE'].includes(currentOptions.method) && 
    typeof currentOptions.body === 'object' && 
    currentOptions.body !== null &&
    !isFormData
  ) {
    try {
      currentOptions.body = JSON.stringify(currentOptions.body);
    } catch (stringifyError) {
       console.error('[apiRequest] Failed to stringify body:', stringifyError);
       throw new Error('Failed to prepare request data.');
    }
  }

  try {
    const response = await fetch(url, currentOptions);
    
    // 1. Parse the response body first (if JSON)
    let responseData = null;
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1 && response.status !== 204) {
         responseData = await response.json();
    }

    // 2. Handle 401 (Unauthorized) specifically
    if (response.status === 401) {
        // Case A: Login Failure (Wrong Password/Email)
        // We want to show the specific message from the backend
        if (endpoint.includes('/login')) {
            const errorMessage = responseData?.message || 'Invalid credentials.';
            const error = new Error(errorMessage);
            error.status = 401;
            throw error;
        }

        // Case B: Actual Session Expiry (Token invalid on protected route)
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        
        if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
        }
        
        const error = new Error('Session expired. Please log in again.');
        error.status = 401;
        throw error;
    }

    // 3. Handle other errors
    if (!response.ok) {
      const errorMessage = responseData?.message || `API request failed with status ${response.status}`;
      const error = new Error(errorMessage);
      error.status = response.status;
      error.data = responseData;
      throw error;
    }

    return responseData;

  } catch (error) {
    console.error('[apiRequest] Request failed:', error.message);
    throw error;
  }
};

export const apiClient = {
  get: (endpoint, options = {}) =>
    apiRequest(endpoint, { ...options, method: 'GET' }),

  post: (endpoint, data, options = {}) =>
    apiRequest(endpoint, {
      ...options,
      method: 'POST',
      body: data 
    }),

  put: (endpoint, data, options = {}) =>
    apiRequest(endpoint, {
      ...options,
      method: 'PUT',
      body: data 
    }),

  delete: (endpoint, options = {}) =>
    apiRequest(endpoint, { ...options, method: 'DELETE' }),
};